import type { AuditEntry } from '../../hooks/useAgreements';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

interface DiffViewerProps {
  entries: AuditEntry[];
}

function parseSafe(json: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function DiffRow({ fieldName, before, after }: { fieldName: string; before: unknown; after: unknown }) {
  return (
    <TableRow>
      <TableCell sx={{ px: 1.5, py: 0.5 }}>
        <Typography variant="caption" fontWeight={500} color="text.secondary">
          {fieldName}
        </Typography>
      </TableCell>
      <TableCell sx={{ px: 1.5, py: 0.5 }}>
        <Typography variant="caption" color="error" sx={{ textDecoration: 'line-through' }}>
          {String(before ?? '—')}
        </Typography>
      </TableCell>
      <TableCell sx={{ px: 1.5, py: 0.5 }}>
        <Typography variant="caption" color="success.main">
          {String(after ?? '—')}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function AuditEntryCard({ entry }: { entry: AuditEntry }) {
  const before = parseSafe(entry.BeforeJson);
  const after = parseSafe(entry.AfterJson);
  const fields = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500} component="span">
            {entry.Action}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
            av {entry.ActorDisplayName || 'Ukjent'}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" component="time">
          {new Date(entry.At).toLocaleString('nb-NO')}
        </Typography>
      </Box>
      {fields.size > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ px: 1.5, py: 0.5 }}>
                <Typography variant="caption" color="text.disabled">Felt</Typography>
              </TableCell>
              <TableCell sx={{ px: 1.5, py: 0.5 }}>
                <Typography variant="caption" color="text.disabled">For</Typography>
              </TableCell>
              <TableCell sx={{ px: 1.5, py: 0.5 }}>
                <Typography variant="caption" color="text.disabled">Etter</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...fields].map((f) => (
              <DiffRow key={f} fieldName={f} before={before?.[f]} after={after?.[f]} />
            ))}
          </TableBody>
        </Table>
      )}
      {fields.size === 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 2, py: 1 }}>
          Ingen feltendringer registrert
        </Typography>
      )}
    </Paper>
  );
}

export function DiffViewer({ entries }: DiffViewerProps) {
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
        Ingen endringshistorikk
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {entries.map((entry) => (
        <AuditEntryCard key={entry.AuditId} entry={entry} />
      ))}
    </Box>
  );
}
